import { useRef } from 'react';
import { Award, Heart, Gem, Download, QrCode } from 'lucide-react';
import { Button } from './ui/button';

// Badge colors mapping (background gradients for certificates)
const BADGE_GRADIENTS = {
  pink: 'from-pink-100 to-pink-200 border-pink-300',
  blue: 'from-blue-100 to-blue-200 border-blue-300',
  amber: 'from-amber-100 to-amber-200 border-amber-300',
  gray: 'from-gray-100 to-gray-200 border-gray-300',
  yellow: 'from-yellow-100 to-yellow-200 border-yellow-300',
  emerald: 'from-emerald-100 to-emerald-200 border-emerald-300',
  red: 'from-red-100 to-red-200 border-red-300'
};

const BADGE_ICON_COLORS = {
  pink: 'text-pink-600',
  blue: 'text-blue-600',
  amber: 'text-amber-700',
  gray: 'text-gray-600',
  yellow: 'text-yellow-600',
  emerald: 'text-emerald-600',
  red: 'text-red-600'
};

const BADGE_ICONS = {
  heart: Heart,
  award: Award,
  gem: Gem
};

// Specialty translations
const SPECIALTY_LABELS = {
  therapeute: 'Thérapeute',
  naturopathe: 'Naturopathe',
  sophrologue: 'Sophrologue',
  psychologue: 'Psychologue',
  osteopathe: 'Ostéopathe',
  hypnotherapeute: 'Hypnothérapeute',
  coach: 'Coach de vie',
  orthophoniste: 'Orthophoniste',
  nutritionniste: 'Nutritionniste'
};

/**
 * Badge visuel avec nom du praticien et spécialité
 * Peut être affiché seul ou dans un document
 */
const PractitionerBadge = ({ badge, practitioner, size = 'md' }) => {
  const Icon = BADGE_ICONS[badge.icon] || Award;
  const gradientClass = BADGE_GRADIENTS[badge.color] || BADGE_GRADIENTS.blue;
  const iconColorClass = BADGE_ICON_COLORS[badge.color] || BADGE_ICON_COLORS.blue;
  
  const sizeClasses = {
    sm: 'w-40 p-3',
    md: 'w-56 p-4',
    lg: 'w-72 p-6'
  };
  
  const iconSizes = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };
  
  const textSizes = {
    sm: { name: 'text-sm', specialty: 'text-xs', badge: 'text-xs' },
    md: { name: 'text-base', specialty: 'text-sm', badge: 'text-sm' },
    lg: { name: 'text-lg', specialty: 'text-base', badge: 'text-base' }
  };

  const firstName = practitioner?.user?.first_name || practitioner?.first_name || '';
  const lastName = practitioner?.user?.last_name || practitioner?.last_name || '';
  const specialty = practitioner?.specialty || '';

  return (
    <div className={`bg-gradient-to-br ${gradientClass} rounded-2xl border-2 shadow-lg ${sizeClasses[size]} flex flex-col items-center text-center`}>
      {/* Icon */}
      <div className={`${iconColorClass} mb-2`}>
        <Icon className={iconSizes[size]} strokeWidth={1.5} />
      </div>
      
      {/* Practitioner name */}
      <p className={`font-bold ${textSizes[size].name} text-gray-900`}>
        Pr. {firstName} {lastName}
      </p>
      
      {/* Specialty */}
      <p className={`${textSizes[size].specialty} text-gray-600 mt-0.5`}>
        {SPECIALTY_LABELS[specialty] || specialty}
      </p>
      
      {/* Badge name */}
      <div className={`mt-2 px-3 py-1 rounded-full bg-white/60 ${textSizes[size].badge} font-semibold ${iconColorClass}`}>
        {badge.name}
      </div>
    </div>
  );
};

/**
 * Document A4 téléchargeable avec badges et QR Code
 */
const PractitionerCertificateA4 = ({ badges, practitioner, onDownload }) => {
  const certificateRef = useRef(null);
  
  const firstName = practitioner?.user?.first_name || practitioner?.first_name || '';
  const lastName = practitioner?.user?.last_name || practitioner?.last_name || '';
  const specialty = practitioner?.specialty || '';
  const practitionerId = practitioner?.id || '';
  
  // QR Code URL pointing to practitioner's profile
  const profileUrl = `https://apora.net/practitioner/${practitionerId}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(profileUrl)}`;

  const handlePrint = () => {
    const printContent = certificateRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Certificat Opora Vie - Pr. ${firstName} ${lastName}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: white;
            color: #1a1a1a;
          }
          .certificate {
            width: 100%;
            min-height: 100vh;
            padding: 40px;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
          }
          .logo {
            font-size: 32px;
            font-weight: bold;
            color: #4A7C59;
            margin-bottom: 8px;
          }
          .subtitle {
            font-size: 14px;
            color: #666;
          }
          .main-title {
            font-size: 28px;
            font-weight: bold;
            color: #333;
            margin: 30px 0 10px;
          }
          .practitioner-name {
            font-size: 36px;
            font-weight: bold;
            color: #1a1a1a;
            margin-bottom: 8px;
          }
          .specialty {
            font-size: 20px;
            color: #4A7C59;
            margin-bottom: 40px;
          }
          .badges-section {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 20px;
            margin-bottom: 50px;
          }
          .badge-item {
            padding: 20px;
            border-radius: 16px;
            border: 2px solid;
            text-align: center;
            min-width: 160px;
          }
          .badge-icon { font-size: 40px; margin-bottom: 8px; }
          .badge-name { font-size: 18px; font-weight: bold; }
          .badge-desc { font-size: 12px; color: #666; margin-top: 4px; }
          .badge-pink { background: linear-gradient(135deg, #fce7f3, #fbcfe8); border-color: #f9a8d4; }
          .badge-blue { background: linear-gradient(135deg, #dbeafe, #bfdbfe); border-color: #93c5fd; }
          .badge-amber { background: linear-gradient(135deg, #fef3c7, #fde68a); border-color: #fcd34d; }
          .badge-gray { background: linear-gradient(135deg, #f3f4f6, #e5e7eb); border-color: #d1d5db; }
          .badge-yellow { background: linear-gradient(135deg, #fef9c3, #fef08a); border-color: #fde047; }
          .badge-emerald { background: linear-gradient(135deg, #d1fae5, #a7f3d0); border-color: #6ee7b7; }
          .badge-red { background: linear-gradient(135deg, #fee2e2, #fecaca); border-color: #fca5a5; }
          .qr-section {
            text-align: center;
            margin-top: auto;
            padding-top: 30px;
            border-top: 1px solid #e5e7eb;
          }
          .qr-title {
            font-size: 14px;
            color: #666;
            margin-bottom: 15px;
          }
          .qr-code {
            width: 150px;
            height: 150px;
          }
          .profile-url {
            font-size: 12px;
            color: #4A7C59;
            margin-top: 10px;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #999;
          }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="header">
            <div class="logo">Opora Vie</div>
            <div class="subtitle">Plateforme de mise en relation avec des praticiens du bien-être</div>
          </div>
          
          <div class="main-title">Certificat de praticien</div>
          
          <div class="practitioner-name">Pr. ${firstName} ${lastName}</div>
          <div class="specialty">${SPECIALTY_LABELS[specialty] || specialty}</div>
          
          <div class="badges-section">
            ${badges.map(badge => `
              <div class="badge-item badge-${badge.color}">
                <div class="badge-icon">${badge.icon === 'heart' ? '❤️' : badge.icon === 'gem' ? '💎' : '🏆'}</div>
                <div class="badge-name">${badge.name}</div>
                <div class="badge-desc">${badge.description}</div>
              </div>
            `).join('')}
          </div>
          
          <div class="qr-section">
            <div class="qr-title">Scannez pour voir mon profil</div>
            <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
            <div class="profile-url">${profileUrl}</div>
          </div>
          
          <div class="footer">
            © ${new Date().getFullYear()} Opora Vie - apora.net
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div 
        ref={certificateRef}
        className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-sage-600">Opora Vie</h2>
          <p className="text-xs text-gray-500">Praticiens du bien-être</p>
        </div>
        
        {/* Practitioner Info */}
        <div className="text-center mb-6">
          <p className="text-xl font-bold text-gray-900">
            Pr. {firstName} {lastName}
          </p>
          <p className="text-sage-600 font-medium">
            {SPECIALTY_LABELS[specialty] || specialty}
          </p>
        </div>
        
        {/* Badges */}
        {badges && badges.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {badges.map(badge => (
              <PractitionerBadge 
                key={badge.id} 
                badge={badge} 
                practitioner={practitioner}
                size="sm"
              />
            ))}
          </div>
        )}
        
        {/* QR Code */}
        <div className="text-center pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Scannez pour voir mon profil</p>
          <img 
            src={qrCodeUrl} 
            alt="QR Code" 
            className="w-24 h-24 mx-auto rounded-lg"
          />
          <p className="text-xs text-sage-600 mt-1 truncate">{profileUrl}</p>
        </div>
      </div>
      
      {/* Download Button */}
      <Button 
        onClick={handlePrint}
        className="w-full bg-sage-600 hover:bg-sage-700"
      >
        <Download className="w-4 h-4 mr-2" />
        Télécharger le certificat (PDF A4)
      </Button>
    </div>
  );
};

/**
 * Mini badge pour affichage sur cartes (avec nom et spécialité)
 */
const PractitionerBadgeMini = ({ badge, practitioner }) => {
  const Icon = BADGE_ICONS[badge.icon] || Award;
  const iconColorClass = BADGE_ICON_COLORS[badge.color] || BADGE_ICON_COLORS.blue;
  
  const firstName = practitioner?.user?.first_name || practitioner?.first_name || '';
  const lastName = practitioner?.user?.last_name || practitioner?.last_name || '';
  const specialty = practitioner?.specialty || '';

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${BADGE_GRADIENTS[badge.color]} border`}>
      <Icon className={`w-4 h-4 ${iconColorClass}`} />
      <div className="text-left">
        <p className="text-xs font-semibold text-gray-900 leading-tight">
          Pr. {firstName} {lastName}
        </p>
        <p className="text-[10px] text-gray-600 leading-tight">
          {SPECIALTY_LABELS[specialty] || specialty}
        </p>
      </div>
    </div>
  );
};

export { 
  PractitionerBadge, 
  PractitionerCertificateA4, 
  PractitionerBadgeMini,
  SPECIALTY_LABELS,
  BADGE_GRADIENTS,
  BADGE_ICONS 
};
